import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { LoaderCircle } from "lucide-react";
import { ZodError } from "zod";
import { useState, useEffect } from "react";
import { agentValidators } from "@shared/validators/admin/agents/agent-validators";
import { agentRegistry } from "@shared/config/agent-registry";
import { WebConnectionForm } from "./WebConnectionForm";
import { ApiConnectionForm, ApiTestButton } from "./ApiConnectionForm";
import UserGroupSelect from "@/components/selects/UserGroupSelect";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";

type ConnectionFormProps = {
    agentId: string;
    agentType: string;
    connection?: any; // For editing existing connections
    onSuccess: (data?: any) => void;
};

const ConnectionForm = ({ agentId, agentType, connection, onSuccess }: ConnectionFormProps) => {
    const [loading, setLoading] = useState(false);
    const [, setPlaceholders] = useState<Record<string, string>>({});
    const utils = trpc.useUtils();
    const { t } = useTranslation();

    // Get agent type configuration from registry
    const agentConfig = agentRegistry[agentType as keyof typeof agentRegistry];
    const isEditMode = !!connection;

    useEffect(() => {
        if (agentConfig?.placeholders) {
            setPlaceholders(agentConfig.placeholders);
        }
    }, [agentConfig]);

    const form = useForm({
        resolver: zodResolver(
            agentValidators.createAgentConnection.extend({
                config: agentConfig?.configSchema || agentValidators.createAgentConnection.shape.config,
            })
        ),
        mode: "onChange", // Validate on change to show errors immediately
        defaultValues: {
            agentId,
            name: connection?.name || "",
            description: connection?.description || "",
            type: agentType,
            config: connection?.config || (agentType == "web" ? { defaultSearchBackend: "tavily" } : {}),
            enabled: connection?.enabled ?? true,
            isDefault: connection?.isDefault ?? false,
            userGroupIds: connection?.userGroupIds || [], // Use existing groups or empty array
        },
    });

    const createConnectionMutation = trpc.agentConnections.create.useMutation({
        onSuccess: () => {
            toast.success(t("connections.connectionCreatedSuccessfully"));
            utils.agentConnections.getForAgents.invalidate();
            onSuccess();
        },
        onError: (error) => {
            toast.error(error.message || t("connections.failedToCreateConnection"));
        },
        onSettled: () => {
            setLoading(false);
        },
    });

    const updateConnectionMutation = trpc.agentConnections.update.useMutation({
        onSuccess: () => {
            toast.success(t("connections.connectionUpdatedSuccessfully"));
            utils.agentConnections.getForAgents.invalidate();
            onSuccess();
        },
        onError: (error) => {
            toast.error(error.message || t("connections.failedToUpdateConnection"));
        },
        onSettled: () => {
            setLoading(false);
        },
    });

    const onSubmit = async (data: any) => {
        setLoading(true);

        if (isEditMode && connection) {
            // For update, we need to pass the connection ID and data
            updateConnectionMutation.mutate({
                id: connection.id,
                data: {
                    name: data.name,
                    description: data.description,
                    config: data.config,
                    enabled: data.enabled,
                    isDefault: data.isDefault,
                    userGroupIds: data.userGroupIds, // Include user groups in update
                },
            });
        } else {
            // For create
            createConnectionMutation.mutate(data);
        }
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            // Get the current values
            const values = form.getValues();

            // Manually validate and set errors for required fields
            let hasErrors = false;

            if (!values.name || values.name.trim() === "") {
                form.setError("name", {
                    type: "manual",
                    message: t("connections.connectionNameRequired") || "Name ist erforderlich",
                });
                hasErrors = true;
            }

            if (!values.description || values.description.trim() === "") {
                form.setError("description", {
                    type: "manual",
                    message: t("connections.descriptionRequired") || "Beschreibung ist erforderlich",
                });
                hasErrors = true;
            }

            if (!values.userGroupIds || values.userGroupIds.length === 0) {
                form.setError("userGroupIds", {
                    type: "manual",
                    message: t("connections.selectAtLeastOneUserGroup") || "Mindestens eine Benutzergruppe auswählen",
                });
                hasErrors = true;
            }

            if (hasErrors) {
                toast.error(t("connections.pleaseFixValidationErrors") || "Bitte fülle alle Pflichtfelder aus");
                return;
            }

            // Validate agent configuration schema manually and map errors
            if (agentConfig?.configSchema) {
                const validation = agentConfig.configSchema.safeParse(values.config);

                if (!validation.success) {
                    validation.error.issues.forEach((issue) => {
                        // Build RHF field name (e.g., "config.atlassianBaseUrl")
                        const issuePath = issue.path && issue.path.length > 0 ? issue.path.join(".") : "";
                        const fieldName = issuePath ? `config.${issuePath}` : "config";
                        form.setError(fieldName as any, {
                            type: "manual",
                            message: issue.message === "Invalid input: expected string, received undefined" ? "Required" : issue.message || t("connections.invalidConfigValue") || "Invalid value",
                        });
                    });

                    toast.error(t("connections.pleaseFixValidationErrors") || "Bitte fülle alle Pflichtfelder aus");
                    return;
                }
            }

            // If no basic validation errors, proceed with form submission
            await form.handleSubmit(onSubmit)(e);
        } catch (error) {
            console.error("Form validation error:", error);

            // If Zod throws a validation error (happens when the resolver validates 'config'),
            // map the Zod issues to react-hook-form errors so they appear next to the inputs.
            if (error instanceof ZodError) {
                // Clear any existing config errors so we don't mix stale errors
                // (iterate over issues and set fields individually below)

                error.issues.forEach((issue) => {
                    // If the issue path is nested like ['config','atlassianBaseUrl']
                    // build the RHF field name using dot notation
                    const fieldPath = issue.path && issue.path.length > 0 ? issue.path.join(".") : undefined;

                    // If the path is something like "config.atlassianBaseUrl" we want the field to be
                    // `config.atlassianBaseUrl` in RHF. Zod will include 'config' in the path if the
                    // schema used includes the config key. Otherwise it might only include nested keys.
                    const rhfFieldName = fieldPath || "config";

                    // Convert issues with array-style paths to dot notation for RHF
                    const rhfFieldDotName = rhfFieldName.replace(/\[|\]/g, "").replace(/\//g, ".");

                    try {
                        form.setError(rhfFieldDotName as any, {
                            type: "manual",
                            message: issue.message || "Invalid value",
                        });
                    } catch (err) {
                        // setError can fail if the path isn't registered; ignore that case.
                        console.warn("Failed to set error for field:", rhfFieldDotName, err);
                    }
                });

                toast.error(t("connections.pleaseFixValidationErrors") || "Please fix validation errors");
                return;
            }
            toast.error(t("connections.pleaseFixValidationErrors") || "Bitte fülle alle Pflichtfelder aus");
        }
    };

    // Render form fields based on agent configuration
    const renderConfigFields = () => {
        if (!agentConfig?.placeholders) return null;
        if (agentType === "web") return <WebConnectionForm form={form} placeholders={agentConfig.placeholders} />;
        if (agentType === "api") return <ApiConnectionForm form={form} placeholders={agentConfig.placeholders} />;
        return Object.entries(agentConfig.placeholders).map(([key, placeholder]) => {
            return (
                <FormField
                    key={key}
                    control={form.control}
                    name={`config.${key}`}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>
                                {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1")}
                            </FormLabel>
                            <FormControl>
                                <Input
                                    placeholder={placeholder}
                                    type={
                                        (key.toLowerCase().includes("key") && key.toLowerCase().includes("api")) ||
                                        key.toLowerCase().includes("token") ||
                                        key.toLowerCase().includes("password")
                                            ? "password"
                                            : "text"
                                    }
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            );
        });
    };

    return (
        <Form {...form}>
            <form onSubmit={handleFormSubmit}>
                <div className="flex gap-4">
                    <div className="flex-1 space-y-3">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("connections.connectionName")}</FormLabel>
                                    <FormControl>
                                        <Input placeholder={t("connections.connectionNamePlaceholder")} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("connections.description")}</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder={t("connections.connectionDescriptionPlaceholder")}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="userGroupIds"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("connections.userGroups")}</FormLabel>
                                    <FormControl>
                                        <UserGroupSelect value={field.value || []} onChange={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="space-y-2">
                            <Label>Configuration</Label>
                            <div className="space-y-3">
                                <FormField
                                    control={form.control}
                                    name="isDefault"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                                            <div className="space-y-0.5">
                                                <FormLabel>{t("connections.setAsDefault")}</FormLabel>
                                                <div className="text-sm text-muted-foreground">
                                                    {t("connections.useConnectionAsDefault")}
                                                </div>
                                            </div>
                                            <FormControl>
                                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="enabled"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                                            <div className="space-y-0.5">
                                                <FormLabel>{t("connections.enabled")}</FormLabel>
                                                <div className="text-sm text-muted-foreground">
                                                    {t("connections.enableConnectionDescription")}
                                                </div>
                                            </div>
                                            <FormControl>
                                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    {/* Dynamic configuration fields based on agent type */}
                    <div className="max-h-[75vh] flex-1 space-y-3 overflow-y-auto">{renderConfigFields()}</div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                    {agentType === "api" && <ApiTestButton form={form} />}
                    <Button type="submit" disabled={loading}>
                        {loading && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
                        {isEditMode ? t("connections.updateConnection") : t("connections.createConnection")}
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default ConnectionForm;
